import type { ClassMember } from "../networking/schoolClasses";
import { toEpochMs } from "../utils/dateUtils";
import { getParsedLocalStorageRecord } from "./LocalStorageHelper";

const DISMISSED_STORAGE_KEY = "classMemberDuplicatesDismissed";

export const normalizeClassMemberName = (name: string): string =>
    name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\./g, "")
        .replace(/\s+/g, " ")
        .trim();

/**
 * Same person when every token pair (up to the shorter name's length) has one token prefixing the
 * other ("lena l" / "lena lehmann", "mimi" / "mimi m", "max" / "maximilian schmidt"). Different
 * surnames sharing an initial ("lena lehmann" / "lena lorenz") stay apart.
 */
export const areLikelySamePerson = (a: string, b: string): boolean => {
    const tokensA = normalizeClassMemberName(a).split(" ").filter(Boolean);
    const tokensB = normalizeClassMemberName(b).split(" ").filter(Boolean);
    if (tokensA.length === 0 || tokensB.length === 0) return false;

    const sharedLength = Math.min(tokensA.length, tokensB.length);
    return tokensA
        .slice(0, sharedLength)
        .every((token, index) => token.startsWith(tokensB[index]) || tokensB[index].startsWith(token));
};

const byCreatedDateThenName = (a: ClassMember, b: ClassMember) =>
    (toEpochMs(a.createdDate) ?? 0) - (toEpochMs(b.createdDate) ?? 0) || a.name.localeCompare(b.name);

/**
 * Greedy: a member joins the first group whose every member it matches, so a bare first name
 * ("lena") can't chain "lena lehmann" and "lena meier" into one group.
 */
export const findDuplicateGroups = (members: ClassMember[]): ClassMember[][] => {
    const groups: ClassMember[][] = [];
    members.forEach((member) => {
        const group = groups.find((candidates) =>
            candidates.every((candidate) => areLikelySamePerson(candidate.name, member.name))
        );
        if (group) {
            group.push(member);
        } else {
            groups.push([member]);
        }
    });

    return groups
        .filter((group) => group.length > 1)
        .map((group) => [...group].sort(byCreatedDateThenName))
        .sort((a, b) => a[0].name.localeCompare(b[0].name));
};

export const duplicateGroupKey = (group: ClassMember[]): string =>
    group
        .map((member) => member.id)
        .sort()
        .join("|");

type DismissedByClass = Record<string, string[]>;

const readAllDismissed = (): DismissedByClass => getParsedLocalStorageRecord<DismissedByClass>(DISMISSED_STORAGE_KEY);

export const readDismissedDuplicateKeys = (classToken: string): string[] => {
    const keys = readAllDismissed()[classToken];
    return Array.isArray(keys) ? keys : [];
};

/** Keyed by member ids, so a group that gains or loses a member counts as a new one and shows again. */
export const dismissDuplicateGroup = (classToken: string, group: ClassMember[]): void => {
    const existing = readDismissedDuplicateKeys(classToken);
    const key = duplicateGroupKey(group);
    if (existing.includes(key)) return;
    localStorage.setItem(
        DISMISSED_STORAGE_KEY,
        JSON.stringify({ ...readAllDismissed(), [classToken]: [...existing, key] })
    );
};
